/**
 * MetricCard Component
 * Standardized metric display card with trend indicators
 * Based on LifeScribe Design System v4.0
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colors, typography, spacing, shadows } from '../../theme/designTokens';
import { icons } from '../../theme/icons';

export type TrendDirection = 'up' | 'down' | 'flat';
export type MetricColorTheme = 'revenue' | 'users' | 'content' | 'costs' | 'default';

export interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  trend?: TrendDirection;
  color?: MetricColorTheme;
  icon?: keyof typeof icons;
  subtitle?: string;
  loading?: boolean;
  prefix?: string;
  suffix?: string;
  precision?: number;
  variant?: 'default' | 'compact';
}

const StyledCard = styled(Card)<{ $compact?: boolean }>(({ theme, $compact }) => ({
  borderRadius: '16px',
  boxShadow: shadows.sm,
  transition: 'all 200ms ease-in-out',
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  minHeight: $compact ? 100 : 160,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: shadows.md,
    transform: 'translateY(-2px)',
  },
}));

const IconContainer = styled(Box)<{ themeColor: MetricColorTheme; $compact?: boolean }>(({ theme, themeColor, $compact }) => {
  const colorMap = {
    revenue: colors.revenue.main,
    users: colors.users.main,
    content: colors.content.main,
    costs: colors.costs.main,
    default: colors.deepTeal[500],
  };

  const bgColorMap = {
    revenue: theme.palette.mode === 'dark' ? 'rgba(20, 97, 82, 0.2)' : colors.revenue[50],
    users: theme.palette.mode === 'dark' ? 'rgba(26, 95, 122, 0.2)' : colors.users[50],
    content: theme.palette.mode === 'dark' ? 'rgba(212, 172, 13, 0.2)' : colors.content[50],
    costs: theme.palette.mode === 'dark' ? 'rgba(125, 25, 53, 0.2)' : colors.costs[50],
    default: theme.palette.mode === 'dark' ? 'rgba(26, 95, 122, 0.2)' : colors.deepTeal[50],
  };

  return {
    width: $compact ? 32 : 48,
    height: $compact ? 32 : 48,
    borderRadius: $compact ? '8px' : '12px',
    backgroundColor: bgColorMap[themeColor],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colorMap[themeColor],
    fontSize: $compact ? '16px' : '24px',
    flexShrink: 0,
  };
});

const TrendChip = styled(Chip)<{ trend: TrendDirection }>(({ theme, trend }) => {
  const isDark = theme.palette.mode === 'dark';
  
  const colorMap = {
    up: {
      bg: isDark ? 'rgba(20, 97, 82, 0.3)' : colors.success[50],
      text: isDark ? colors.success.light : colors.success.dark,
      border: colors.success.light,
    },
    down: {
      bg: isDark ? 'rgba(125, 25, 53, 0.3)' : colors.error[50],
      text: isDark ? colors.error.light : colors.error.dark,
      border: colors.error.light,
    },
    flat: {
      bg: isDark ? 'rgba(107, 114, 128, 0.3)' : colors.gray[100],
      text: isDark ? colors.gray[300] : colors.gray[700],
      border: isDark ? colors.gray[600] : colors.gray[300],
    },
  };

  const { bg, text, border } = colorMap[trend];

  return {
    backgroundColor: bg,
    color: text,
    borderColor: border,
    fontWeight: typography.weights.semibold,
    fontSize: typography.sizes.xs,
    height: 24,
  };
});

/**
 * Format number with abbreviations (K, M, B)
 */
const formatNumber = (value: number, precision: number = 0): string => {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(precision)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(precision)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(precision)}K`;
  }
  return value.toFixed(precision);
};

/**
 * Get trend icon component
 */
const getTrendIcon = (trend: TrendDirection) => {
  switch (trend) {
    case 'up':
      return TrendingUp;
    case 'down':
      return TrendingDown;
    case 'flat':
      return TrendingFlat;
  }
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  trend = 'flat',
  color = 'default',
  icon,
  subtitle,
  loading = false,
  prefix = '',
  suffix = '',
  precision = 0,
  variant = 'default',
}) => {
  const theme = useTheme();
  const compact = variant === 'compact';

  // Format value
  const formattedValue = typeof value === 'number'
    ? formatNumber(value, precision)
    : value;

  // Get icon component
  const MaterialIcon = icon ? icons[icon] : null;
  const TrendIcon = getTrendIcon(trend);

  if (loading) {
    return (
      <StyledCard $compact={compact}>
        <CardContent
          sx={{
            p: compact ? spacing[3] : spacing[5],
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: spacing[3] }}>
            <Skeleton variant="rounded" width={compact ? 32 : 48} height={compact ? 32 : 48} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="70%" height={14} />
              <Skeleton variant="text" width="50%" height={compact ? 22 : 36} sx={{ mt: spacing[1] }} />
            </Box>
          </Box>
          {!compact && <Skeleton variant="text" width="40%" height={20} sx={{ mt: spacing[2] }} />}
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard $compact={compact}>
      <CardContent
        sx={{
          p: compact ? spacing[3] : spacing[5],
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: compact ? spacing[2] : spacing[3] }}>
          {/* Icon */}
          {MaterialIcon && (
            <IconContainer themeColor={color} $compact={compact}>
              <span className="material-icons" style={{ fontSize: compact ? 16 : 24 }}>
                {MaterialIcon}
              </span>
            </IconContainer>
          )}

          {/* Content */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              variant="body2"
              sx={{
                fontFamily: typography.fonts.heading,
                fontSize: typography.sizes.xs,
                fontWeight: typography.weights.medium,
                color: 'text.secondary',
                mb: compact ? 0 : spacing[1],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>

            {/* Value */}
            <Typography
              variant={compact ? 'h5' : 'h3'}
              sx={{
                fontFamily: typography.fonts.display,
                fontSize: compact
                  ? typography.sizes.xl
                  : { xs: typography.sizes['2xl'], sm: typography.sizes['3xl'], md: typography.sizes['4xl'] },
                fontWeight: typography.weights.bold,
                color: 'text.primary',
                lineHeight: 1.1,
                mb: compact ? 0 : spacing[1],
                wordBreak: 'break-word',
              }}
            >
              {prefix}{formattedValue}{suffix}
            </Typography>
          </Box>
        </Box>

        {/* Subtitle and Trend — omit in compact to save vertical space */}
        {!compact && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing[2], flexWrap: 'wrap', mt: spacing[2] }}>
            {change !== undefined && (
              <TrendChip
                trend={trend}
                size="small"
                icon={<TrendIcon style={{ fontSize: 14 }} />}
                label={`${change > 0 ? '+' : ''}${typeof change === 'number' ? change.toFixed(1) : change}%`}
              />
            )}
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: typography.sizes.xs,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {/* Compact: subtitle only (no trend chip) — keeps tile informative */}
        {compact && subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '11px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mt: '2px',
              display: 'block',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default MetricCard;
